import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import renderTooltip from '../../../components/siteadmin/SiteSettingsForm/toolTipHelper.js';

import CurrencyConverter from '../../CurrencyConverter';
import Loader from '../../Loader/Loader.js';
import { getBillingDetails } from '../../../helpers/Pricing/getBillingDetails.js';
import messages from '../../../locale/messages';

import s from '../ViewMessage.css';

const PaymentDetails = ({
	startDate,
	endDate,
	monthlyDiscount,
	weeklyDiscount,
	userType,
	reservationData,
	getContactHostThreadDetails,
	listingCurrency,
	loading,
}) => {

	const { formatMessage } = useIntl();

	let guestServiceFee = 0, hostServiceFee = 0, taxPrice = 0, isCleaningPrice = 0, taxValue = 0;
	let priceForDays = 0, discount = 0, discountType, total = 0, hostEarnings = 0;
	let currency = reservationData?.currency ? reservationData?.currency : listingCurrency, petValue = 0;

	let { dayDifference, dayPrice, isAverage, guestTotal, hostTotal, isSpecialPricingAssigned, hostSubTotal } = getBillingDetails({
		checkIn: startDate, checkOut: endDate, bookingSpecialPricing: reservationData?.bookingSpecialPricing,
		basePrice: reservationData?.basePrice, total: reservationData?.total, guestServiceFee: reservationData?.guestServiceFee, hostServiceFee: reservationData?.hostServiceFee, petFee: reservationData?.petFee
	});

	hostEarnings = hostTotal;
	priceForDays = dayPrice;
	discount = reservationData?.discount;
	discountType = reservationData?.discountType;
	isCleaningPrice = reservationData?.cleaningPrice;
	guestServiceFee = reservationData?.guestServiceFee;
	hostServiceFee = reservationData?.hostServiceFee;
	taxPrice = reservationData?.taxPrice;
	taxValue = reservationData?.taxValue;
	total = userType === 'host' ? hostSubTotal : guestTotal;
	petValue = reservationData?.petFee;

	if (!reservationData) {
		priceForDays = getContactHostThreadDetails?.priceForDays;
		dayDifference = getContactHostThreadDetails?.dayDifference;
		isAverage = getContactHostThreadDetails?.isAverage;
		discount = getContactHostThreadDetails?.discount;
		isCleaningPrice = getContactHostThreadDetails?.cleaningPrice;
		taxPrice = getContactHostThreadDetails?.taxPrice;
		guestServiceFee = getContactHostThreadDetails?.guestServiceFee;
		hostServiceFee = getContactHostThreadDetails?.hostServiceFee;
		hostEarnings = getContactHostThreadDetails?.hostTotal;
		isSpecialPricingAssigned = getContactHostThreadDetails?.isSpecialPriceAssigned;
		taxValue = getContactHostThreadDetails?.tax;
		petValue = getContactHostThreadDetails?.petFee || 0;

		if (userType === 'host') {
			total = getContactHostThreadDetails?.totalWithoutFees;
		} else {
			total = getContactHostThreadDetails?.guestTotal;
		}

		if (dayDifference >= 7) {
			if (monthlyDiscount > 0 && dayDifference >= 28) {
				discountType = monthlyDiscount + formatMessage(messages.monthlyPriceDiscount);
			} else {
				if (weeklyDiscount > 0) {
					discountType = weeklyDiscount + formatMessage(messages.weeklyPriceDiscount);
				}
			}
		}
	}

	return (
		<>
			<hr className={s.horizondalLine} />
			<h4 className={cx(s.tripDetailHeading, s.spaceBottom15)}>
				<span><FormattedMessage {...messages.payment} /></span>
			</h4>
			{
				loading ? <Loader type="text" /> :
					<>
						{
							<div className={cx(s.paymentDetailText, s.paymentFlex)}>
								<div className='paymentDetailsFlexLeft'>
									<div className='tooltipFlex'>
										{
											isSpecialPricingAssigned &&
											<>{renderTooltip(formatMessage(messages.averagePricePerNight), 'commonTooltipIcon')}</>
										}
										<div className={cx('directionLtr')}>
											<CurrencyConverter
												amount={isAverage}
												from={currency}
											/>
											{' x'} {dayDifference} {dayDifference > 1 ? formatMessage(messages.nights) : formatMessage(messages.night)}
										</div>
									</div>
								</div>
								<div className='paymentDetailsFlexRight'>
									<span className={cx(s.txtBreak)}>
										<CurrencyConverter
											amount={priceForDays}
											from={currency}
										/>
									</span>
								</div>
							</div>
						}
						{
							isCleaningPrice > 0 && <div className={cx(s.paymentDetailText, s.paymentFlex)}>
								<div className='paymentDetailsFlexLeft'>
									<span ><FormattedMessage {...messages.cleaningFee} /></span>
								</div>
								<div className='paymentDetailsFlexRight'>
									<span className={cx(s.txtBreak)}>
										<CurrencyConverter
											amount={isCleaningPrice}
											from={currency}
										/>
									</span>
								</div>
							</div>
						}
						{
							discount > 0 && <div className={cx(s.paymentDetailText, s.paymentFlex)}>
								<div className='paymentDetailsFlexLeft'>
									<span >{discountType}</span>
								</div>
								<div className='paymentDetailsFlexRight'>
									<span className={cx(s.txtBreak)}>
										- <CurrencyConverter
											amount={discount}
											from={currency}
										/>
									</span>
								</div>
							</div>
						}

						{
							userType === 'guest' && guestServiceFee > 0 && <div className={cx(s.paymentDetailText, s.paymentFlex)}>
								<div className='paymentDetailsFlexLeft'>
									<span><FormattedMessage {...messages.serviceFee} /></span>
								</div>
								<div className='paymentDetailsFlexRight'>
									<span className={cx(s.txtBreak)}>
										<CurrencyConverter
											amount={guestServiceFee}
											from={currency}
										/>
									</span>
								</div>
							</div>
						}
						{
							petValue > 0 && <div className={cx(s.paymentDetailText, s.paymentFlex)}>
								<div className='paymentDetailsFlexLeft'>
									<span><FormattedMessage {...messages.petFee} /></span>
								</div>
								<div className='paymentDetailsFlexRight'>
									<span className={cx(s.txtBreak)}>
										<CurrencyConverter
											amount={petValue}
											from={currency}
										/>
									</span>
								</div>
							</div>
						}
						{
							taxPrice > 0 && <div className={cx(s.paymentDetailText, s.paymentFlex)}>
								<div className='paymentDetailsFlexLeft'>
									<span><FormattedMessage {...messages.tax} /> {taxValue > 0 && `(${taxValue.toFixed(2)}%)`}</span>
								</div>
								<div className='paymentDetailsFlexRight'>
									<span className={cx(s.txtBreak)}>
										<CurrencyConverter
											amount={taxPrice}
											from={currency}
										/>
									</span>
								</div>
							</div>
						}

						{
							userType === 'guest' && <hr className={s.horizondalLine} />
						}

						<div className={cx({ [s.textBold]: userType === 'guest' }, { [s.paymentDetailText]: userType === 'host' }, s.paymentFlex)}>
							<div className='paymentDetailsFlexLeft'>
								<span className={cx({ [s.paymentTotal]: userType === 'guest' })}><FormattedMessage {...messages.subTotal} /></span>
							</div>
							<div className='paymentDetailsFlexRight'>
								<span className={cx({ [s.paymentTotal]: userType === 'guest' }, s.txtBreak)}>
									<CurrencyConverter
										amount={total}
										from={currency}
									/>
								</span>
							</div>
						</div>

						{
							userType === 'host' && hostServiceFee > 0 && <div className={cx(s.paymentDetailText, s.paymentFlex)}>
								<div className='paymentDetailsFlexLeft'>
									<span><FormattedMessage {...messages.serviceFee} /></span>
								</div>
								<div className='paymentDetailsFlexRight'>
									<span>
										-
										<CurrencyConverter
											amount={hostServiceFee}
											from={currency}
										/>
									</span>
								</div>
							</div>
						}

						{
							userType === 'host' && <hr className={s.horizondalLine} />
						}


						{
							userType === 'host' && <div className={cx(s.textBold, s.paymentFlex)}>
								<div className='paymentDetailsFlexLeft'>
									<span className={s.paymentTotal}><FormattedMessage {...messages.youEarn} /></span>
								</div>
								<div className='paymentDetailsFlexRight'>
									<span className={cx(s.paymentTotal, s.txtBreak)}>
										<CurrencyConverter
											amount={hostEarnings}
											from={currency}
										/>
									</span>
								</div>
							</div>
						}
					</>
			}
		</>
	);
}

PaymentDetails.propTypes = {
	formatMessage: PropTypes.any,
	userType: PropTypes.string.isRequired,
	basePrice: PropTypes.number.isRequired,
	cleaningPrice: PropTypes.number.isRequired,
	monthlyDiscount: PropTypes.number,
	weeklyDiscount: PropTypes.number,
	currency: PropTypes.string.isRequired,
	startDate: PropTypes.string.isRequired,
	endDate: PropTypes.string.isRequired
};

PaymentDetails.defaultProps = {
	startDate: null,
	endDate: null,
	basePrice: 0,
	cleaningPrice: 0,
	monthlyDiscount: 0,
	weeklyDiscount: 0
};

export default withStyles(s)(PaymentDetails);